import { useEffect, useRef, useSyncExternalStore } from "react";
import { subscribeOrientation } from "utils/orientation-store";

type OrientationSnapshot = {
    orientation: number;
    portrait: boolean;
};

const defaultSnapshot: OrientationSnapshot = {
    orientation: 0,
    portrait: true,
};

let cachedSnapshot: OrientationSnapshot = defaultSnapshot;

const getOrientationValue = () => {
    if (typeof window === "undefined") {
        return defaultSnapshot.orientation;
    }

    if (typeof window.orientation === "number") {
        return window.orientation;
    }

    const angle = window.screen?.orientation?.angle;

    return typeof angle === "number" ? angle : defaultSnapshot.orientation;
};

const getSnapshot = () => {
    const orientation = getOrientationValue();
    const portrait = Math.abs(orientation) !== 90;

    if (cachedSnapshot.orientation !== orientation || cachedSnapshot.portrait !== portrait) {
        cachedSnapshot = {
            orientation,
            portrait,
        };
    }

    return cachedSnapshot;
};

const getServerSnapshot = () => defaultSnapshot;

export const useOrientation = () =>
    useSyncExternalStore(subscribeOrientation, getSnapshot, getServerSnapshot);

export const useOrientationChange = (callback: () => void, enabled = true) => {
    const { orientation } = useOrientation();
    const prevOrientation = useRef(orientation);
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!enabled) {
            prevOrientation.current = orientation;
            return;
        }

        if (prevOrientation.current === orientation) {
            return;
        }

        prevOrientation.current = orientation;
        callbackRef.current();
    }, [enabled, orientation]);
};
